/**
 * ScenarioSourcePicker.tsx
 * FEAT-2026-0007 Phase 6B.3 — Scenario picker + top filter bar.
 *
 * Rendered inside ScenarioPlanningPage when no active scenario exists.
 * Replaces the dev-only "Preview header chrome" placeholder button.
 *
 * Flow:
 *   1. User picks source type: Quote | WorkOrder
 *   2. User searches / selects a Quote or WorkOrder from the autocomplete
 *   3. On confirm:
 *      a. getActiveScenarioForSource → if a Draft/NeedsReview already exists, resume it
 *      b. Otherwise: createScenarioDraft → generateScheduleTasks (with scenarioKey)
 *   4. Calls onScenarioReady(scenario) → parent sets the active scenario
 *
 * Quote source: reuses getQuotes() from quoteService (returns all QuoteWithClient[]).
 * WorkOrder source: WorkOrders are project-scoped (no list-all endpoint); for now
 *   the WO tab uses a project-key filter.  On this page we provide a free-text ID
 *   entry for WO (phase 6D will deepen this via the deep-link / entry point).
 *
 * Colors: all via theme.schedule.* — no hex literals.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { alpha, useTheme } from '@mui/material/styles';

import type { Scenario } from '../../../../services/scheduleScenario';
import { getQuotes } from '../../../../services/quoteService';
import type { QuoteWithClient } from '../../../../types/quote';
import { resolveScenarioForSource } from './useResolveScenarioForSource';
import { LockedPlanDialog } from './LockedPlanDialog';
import { useLockedPlanChoice } from './useLockedPlanChoice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SourceType = 'Quote' | 'WorkOrder';

interface QuoteOption {
  key: string;
  label: string;
  /** The original QuoteWithClient record, kept for display sub-text. */
  quote: QuoteWithClient;
}

interface ScenarioSourcePickerProps {
  /** Called after the scenario is created/resumed and tasks generated. */
  onScenarioReady: (scenario: Scenario) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Display label for an option in the quote autocomplete. */
function quoteOptionLabel(opt: QuoteOption): string {
  return `${opt.quote.QuoteNumber} — ${opt.quote.ClientName ?? 'No client'} — ${opt.quote.Title ?? '(no title)'}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScenarioSourcePicker: React.FC<ScenarioSourcePickerProps> = ({
  onScenarioReady,
}) => {
  const theme = useTheme();

  // ── Source type toggle ────────────────────────────────────────────────────
  const [sourceType, setSourceType] = useState<SourceType>('Quote');

  // ── Quote autocomplete state ───────────────────────────────────────────────
  const [quotes, setQuotes] = useState<QuoteWithClient[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<QuoteOption | null>(null);
  const [quoteInputValue, setQuoteInputValue] = useState('');

  // ── WorkOrder free-text entry ──────────────────────────────────────────────
  // WO IDs are plain strings (e.g. "WO-2026-123"). For 6B.3 we accept a manual
  // entry; 6D will wire this to a proper deep-link/autocomplete.
  const [workOrderId, setWorkOrderId] = useState('');

  // ── Action state ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  const showSnack = (
    message: string,
    severity: 'success' | 'info' | 'error' = 'info',
  ) => setSnack({ open: true, message, severity });

  // ── Locked-plan choice (2026-07-10 silent-duplicate fix) ──────────────────
  // When the picked source's only plan is Submitted, the resolve returns
  // { kind: 'locked' } — offer Open / Reopen to Edit / Create new draft
  // instead of silently creating a duplicate fully-booked draft.
  const lockedChoice = useLockedPlanChoice({
    onScenarioReady: (scenario, message, severity) => {
      showSnack(message, severity);
      onScenarioReady(scenario);
    },
    onError: (message) => showSnack(message, 'error'),
  });

  // ── Load quotes on mount (and when switching to Quote mode) ───────────────
  useEffect(() => {
    if (sourceType !== 'Quote') return;
    let cancelled = false;

    setQuotesLoading(true);
    setQuotesError(null);

    getQuotes()
      .then((data) => {
        if (!cancelled) {
          // Only show quotes that are scheduleable (have line items and are
          // past draft/internal-review). For the picker we show all non-deleted
          // quotes — the backend will reject generate-tasks for un-approved ones.
          setQuotes(data);
        }
      })
      .catch(() => {
        if (!cancelled) setQuotesError('Failed to load quotes. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setQuotesLoading(false);
      });

    return () => { cancelled = true; };
  }, [sourceType]);

  // ── Derived: quote options for the autocomplete ───────────────────────────
  const quoteOptions: QuoteOption[] = useMemo(
    () =>
      quotes.map((q) => ({
        key: q.QuoteKey,
        label: quoteOptionLabel({ key: q.QuoteKey, label: '', quote: q }),
        quote: q,
      })),
    [quotes],
  );

  // ── Filter options client-side via the input value ────────────────────────
  const filteredOptions = useMemo(() => {
    if (!quoteInputValue.trim()) return quoteOptions;
    const lower = quoteInputValue.toLowerCase();
    return quoteOptions.filter(
      (o) =>
        o.quote.QuoteNumber.toLowerCase().includes(lower) ||
        (o.quote.ClientName?.toLowerCase() ?? '').includes(lower) ||
        (o.quote.Title?.toLowerCase() ?? '').includes(lower),
    );
  }, [quoteOptions, quoteInputValue]);

  // ── Can the confirm button be clicked? ────────────────────────────────────
  const canConfirm =
    !loading &&
    (sourceType === 'Quote'
      ? selectedQuote !== null
      : workOrderId.trim().length > 0);

  // ── Handle confirm ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);

    try {
      const sourceKey =
        sourceType === 'Quote'
          ? selectedQuote!.key
          : workOrderId.trim();

      // Delegate to the shared resolve helper (same logic used by the deep-link path).
      const result = await resolveScenarioForSource(sourceType, sourceKey);

      if (result.kind === 'locked') {
        // The source's only plan is Submitted — never silently create a
        // duplicate. Ask: Open plan / Reopen to Edit / Create new draft.
        lockedChoice.show(result.scenario, sourceType, sourceKey);
        return;
      }

      showSnack(result.message, result.severity);

      // Hand off to the parent
      onScenarioReady(result.scenario);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Unexpected error. Please try again.';
      showSnack(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          maxWidth: 560,
          mx: 'auto',
        }}
      >
        {/* ── Icon + title ───────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <InsightsIcon
            sx={{ fontSize: '2.5rem', color: alpha(theme.schedule.committed, 0.7) }}
          />
          <Typography variant="h6" fontWeight={600} textAlign="center">
            Start a Scenario
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            maxWidth={400}
          >
            Select a Quote or Work Order to plan. If a draft already exists for
            that source, it will be resumed automatically.
          </Typography>
        </Box>

        {/* ── Source type toggle ─────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            PLANNING FOR
          </Typography>
          <ToggleButtonGroup
            value={sourceType}
            exclusive
            onChange={(_e, val) => {
              if (val) {
                setSourceType(val as SourceType);
                setSelectedQuote(null);
                setQuoteInputValue('');
                setWorkOrderId('');
              }
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="Quote" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Quote
            </ToggleButton>
            <ToggleButton value="WorkOrder" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Work Order
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* ── Quote autocomplete ─────────────────────────────────────────── */}
        {sourceType === 'Quote' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              SELECT QUOTE
            </Typography>
            {quotesError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {quotesError}
              </Alert>
            )}
            <Autocomplete<QuoteOption>
              options={filteredOptions}
              loading={quotesLoading}
              value={selectedQuote}
              inputValue={quoteInputValue}
              onInputChange={(_e, val) => setQuoteInputValue(val)}
              onChange={(_e, val) => setSelectedQuote(val)}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.key === val.key}
              renderOption={(props, opt) => (
                <Box
                  component="li"
                  {...props}
                  key={opt.key}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {opt.quote.QuoteNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.quote.ClientName ?? 'No client'}
                    {opt.quote.Title ? ` — ${opt.quote.Title}` : ''}
                    {' '}· {opt.quote.Status}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by quote number, client, or title…"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon
                          fontSize="small"
                          sx={{ mr: 0.5, color: 'text.disabled' }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {quotesLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                quotesLoading ? 'Loading quotes…' : 'No matching quotes found'
              }
            />
          </Box>
        )}

        {/* ── WorkOrder free-text entry ──────────────────────────────────── */}
        {sourceType === 'WorkOrder' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              WORK ORDER ID
            </Typography>
            <TextField
              size="small"
              placeholder="e.g. WO-2026-123"
              value={workOrderId}
              onChange={(e) => setWorkOrderId(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />
                ),
              }}
            />
            <Typography variant="caption" color="text.disabled">
              Enter the Work Order ID to plan. A full WO autocomplete is coming in Phase 6D.
            </Typography>
          </Box>
        )}

        {/* ── Confirm button ────────────────────────────────────────────── */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={!canConfirm}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          onClick={() => { void handleConfirm(); }}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Setting up scenario…' : 'Create / Resume Scenario'}
        </Button>
      </Paper>

      {/* ── Locked-plan choice (picked source has a Submitted plan) ──────── */}
      <LockedPlanDialog
        open={lockedChoice.dialogOpen}
        scenario={lockedChoice.lockedScenario}
        busy={lockedChoice.busy}
        onOpenPlan={lockedChoice.handleOpenPlan}
        onReopen={lockedChoice.handleReopen}
        onCreateNew={lockedChoice.handleCreateNew}
        onClose={lockedChoice.handleClose}
      />

      {/* ── Snackbar ────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};
